import { Button, Dropdown, MenuProps, Space, Tabs, Typography } from "antd";
import { Icon } from "@iconify/react";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { WorkflowReviewer, toArray, workOrderApi, workflowApi } from "../../../Services";

import { ContentWrapper } from "../../../Components/Layout/ContentWrapper";
import { useQuery } from "../../../Hooks/useQuery";
import { hasKeyString } from "../../../Utils/CommonUtils";
import { useNotification } from "../../../Hooks/useNotification";
import { PDFViewer } from "../../../Components/PDFViewer/PDFViewer";
import { createWorkOrderPdf } from "../../../Utils/pdfmake/workOrderPdf";
import { hasApproved } from "../Constants";
import { createDraftWorkOrderPdf } from "../../../Utils/pdfmake/draftWorkOrderPdf";

import { ProjectInformation } from "./Tabs/ProjectInformation";
import { Comments } from "./Tabs/Comments";
import { DocumentVersions } from "./Tabs/DocumentVersions";
import { Logs } from "./Tabs/Logs";
import { OutgoingMail, SendDocumentModal } from "./Tabs/SendDocumentModal";
import { useHasPanelAccess } from "../../../Hooks/useHasAccess";
import { SentSuccessModal } from "./Tabs/SentSuccessModal";
import { workOrderDetail } from "./mockData";
import styles from "../WorkOrder.module.scss";

/**
 * Work Order — detail (drilldown) screen.
 *
 * Same shell as SPADrilldown: ContentWrapper with a back-arrow customTitle and
 * an Actions dropdown, plus URL-driven tabs (`?activeTab=`) so a tab is
 * linkable and survives refresh. The tab strip is pill-styled via
 * `styles.pillTabs` rather than antd's default underline.
 *
 * Tab bodies read from `mockData` until a Work Order detail endpoint exists.
 */
export const WorkOrderDrilldown = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const activeTab = query.get("activeTab") || "projectInformation";
  /**
   * The work order this page is showing.
   *
   * Loaded from GET /work-order/detail?envelopeId= (V3). The route param IS
   * the envelopeId — the lists navigate with it.
   *
   * Comments come from the workflow service (V4); Document Versions and Logs
   * still read `mockData` — no endpoint has been published for either.
   */
  const { id: envelopeId } = useParams();
  const [loadedDetail, setDetail] = useState<any>(workOrderDetail);
  const [isLoading, setIsLoading] = useState(false);
  /* Bumped after a workflow action so the detail and the comment trail
     re-read rather than showing the state from before the action. */
  const [refreshKey, setRefreshKey] = useState(0);
  /* The envelope the documents live under: the record's own envelopeId, which
     need not be the id in the route — and never with a draft's "-draft"
     suffix, which uploads drop too, or a draft's documents are never found.
     Unset until the record answers; the route id if it never does. */
  const documentIdOf = (id?: string) => (id ? id.replace(/-draft$/i, "") : id);
  const [documentEnvelopeId, setDocumentEnvelopeId] = useState<string>();
  /* A draft's current documents are read under its "-draft" id. */
  const [documentsDraft, setDocumentsDraft] = useState(false);
  /* The documents as they stand now (GET /document/get/documents/current-version),
     null until it answers. Once it has, it is what the tabs and the PDF show. */
  const [currentDocuments, setCurrentDocuments] = useState<any[] | null>(null);
  useEffect(() => {
    if (!documentEnvelopeId) return;
    const isDraft =
      documentsDraft ||
      String(loadedDetail?.status ?? "").toUpperCase() === "DRAFT" ||
      /-draft$/i.test(envelopeId ?? "");
    if (isDraft) return;

    workOrderApi
      .currentDocuments(documentEnvelopeId, isDraft)
      .then((data: any) => {
        const list = Array.isArray(data)
          ? data
          : [
              data?.records,
              data?.data,
              data?.documentVersionResponses,
              data?.documents,
              data?.response?.data,
            ].find(Array.isArray) ?? [];
        setCurrentDocuments(list);
      })
      .catch(() => setCurrentDocuments(null));
  }, [documentEnvelopeId, documentsDraft, refreshKey, loadedDetail?.status, envelopeId]);

  const detail = useMemo(
    () =>
      currentDocuments && currentDocuments.length > 0
        ? {
            ...loadedDetail,
            documents: currentDocuments,
            documentVersionResponses: currentDocuments,
            documentVersion: loadedDetail?.documentVersion,
          }
        : loadedDetail,
    [loadedDetail, currentDocuments]
  );
  /* The routing as the workflow service holds it — who reviews and approves,
     each with their status and when they acted. Loaded here, once, so the
     details tab and the PDF show the same chain. */
  const [workflowRows, setWorkflowRows] = useState<WorkflowReviewer[]>([]);

  useEffect(() => {
    if (!envelopeId) return;
    workflowApi
      .reviewers(envelopeId)
      .then((rows: any) => setWorkflowRows(toArray<WorkflowReviewer>(rows)))
      .catch(() => setWorkflowRows([]));
  }, [envelopeId, refreshKey]);

  /* A draft has not been routed yet, so the workflow service has nothing for
     it — its chain is stored on the work order, in draft-create's spelling
     (userName / userEmail / approvingOrder). The workflow service wins
     whenever it has rows. */
  const routing: WorkflowReviewer[] = useMemo(
    () =>
      workflowRows.length
        ? workflowRows
        : toArray<any>(detail?.reviewers).map((row: any) => ({
            ...row,
            reviewerName: row.reviewerName ?? row.userName ?? row.name,
            email: row.email ?? row.userEmail,
            routingOrder: row.routingOrder ?? row.approvingOrder ?? row.order,
          })),
    [workflowRows, detail]
  );

  useEffect(() => {
    if (!envelopeId) return;
    setIsLoading(true);
    workOrderApi
      .detail(envelopeId)
      .then((payload: any) => {
        /* The work order is not always at the top level of the response — it
           arrives under `workOrder`, with `reviewers` as a SIBLING rather than
           a property of it. Reading the wrapper is why every field rendered
           "-" against a record that plainly has values. */
        const record: any = Array.isArray(payload)
          ? payload[0]
          : payload?.workOrder ??
            payload?.workOrderDetails ??
            payload?.detail ??
            payload?.records?.[0] ??
            payload;

        setDocumentsDraft(String(record?.status ?? "").toUpperCase() === "DRAFT" || /-draft$/i.test(envelopeId ?? ""));
        setDocumentEnvelopeId(documentIdOf(record?.envelopeId || payload?.envelopeId || envelopeId));
        if (!record || !Object.keys(record).length) return;
        /* The record is shaped like WorkOrderRequestDto, whose names differ
           from this screen's labels: `division` is Cost Centre, `category` is
           the contract category and the vendor arrives as `vendorName`. Read
           under both, or the fields render as "-" against a record that
           plainly has values. */
        const pick = (...candidates: any[]) =>
          candidates.find((value) => value !== undefined && value !== null && value !== "");

        /* Requester and Date Request are not part of WorkOrderRequestDto, so a
           record written by the wizard carries neither — fall back to who
           created it and when. */
        setDetail({
          ...record,
          /* The routing sits beside the work order, not inside it — and the
             uploaded documents may too, so both places are read. */
          reviewers: payload?.reviewers ?? record.reviewers ?? [],
          documentVersion: payload?.documentVersion ?? record?.documentVersion,
          documentVersionResponses: record.documentVersionResponses ?? payload?.documentVersionResponses,
          lastVersion: record.lastVersion ?? payload?.lastVersion,
          overallStatus: record.overallStatus ?? payload?.overallStatus,
          rejectReason: record.rejectReason ?? payload?.rejectReason,
          cancelReason: record.cancelReason ?? payload?.cancelReason,
          /* Every spelling the edit form already reads the same record under
             (AddWorkOrder), so a draft shows here exactly what reopening it
             in the wizard shows. */
          subsidiary: pick(record.subsidiary, record.subsidiaryName, record.subsidiaryDivision) ?? "",
          costCentre: pick(record.division, record.divisionName, record.costCentre) ?? "",
          category: pick(record.category, record.categoryName, record.contractCategory) ?? "",
          region: pick(record.region, record.regionName) ?? "",
          subRegion: pick(record.subRegion, record.subRegionName) ?? "",
          vendor: pick(record.vendorName, record.vendor) ?? "",
          requesterName: pick(record.requesterName, record.createdByName, record.createdBy) ?? "",
          requesterEmail: pick(record.requesterEmail, record.createdBy) ?? "",
          dateRequest: pick(record.dateRequest, record.createdOn) ?? "",
          jobTitle: pick(record.jobTitle, record.projectTitle) ?? "",
          projectTitle: pick(record.projectTitle, record.jobTitle, "Work Order"),
        });
      })
      .catch(() => {
        // keep the shell; api.service surfaces the error
        setDocumentsDraft(/-draft$/i.test(envelopeId ?? ""));
        setDocumentEnvelopeId(documentIdOf(envelopeId));
      })
      .finally(() => setIsLoading(false));
  }, [envelopeId, refreshKey]);
  /* Mounted at both /workorder/view/:id and /workorder-approval/view/:id. */
  const backTo = hasKeyString("approval")
    ? `/workorder-approval${query.get("from") === "approved" ? "?activeTab=approved" : ""}`
    : "/workorder";

  const [actionsOpen, setActionsOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [preview, setPreview] = useState<{ name: string; url: string } | null>(null);
  const { openNotification } = useNotification();

  /**
   * The work order as a PDF, built in the browser.
   *
   * Same approach as the SPA drilldown: there is no endpoint that returns a
   * rendered work order, so `printPdf` turns the record into a pdfmake
   * definition and the three actions each take it somewhere different —
   * `getBlob` for the viewer, `.print()` for the printer, `getBlob` plus an
   * anchor to save it. `workOrderPdfData` translates the record into the names
   * that generator reads.
   */
  /* The PDF ends with the Comments and Logs tabs, as e-POD's does, so both are
     read first — every row, not the page on screen. A failed read leaves that
     section saying "No … found" rather than blocking the document. */
  const workOrderDocument = async (customDetail?: any) => {
    const [comments, logs] = envelopeId
      ? await Promise.all([
          workflowApi.commentList({ envelopeId, page: 0, size: 1000 }).then(toArray).catch(() => []),
          workOrderApi.logs({ envelopeId, size: 1000 }).then(toArray).catch(() => []),
        ])
      : [[], []];
    return createWorkOrderPdf(customDetail || detail, routing, { comments, logs });
  };
  const documentFailed = () =>
    openNotification({
      title: "Could not produce the document",
      message: "The work order could not be rendered as a PDF.",
      type: "error",
    });

  /* "Draft Work Order" and "Final Work Order" are the same letter to the vendor
     (draftWorkOrderPdf), titled "Draft Work Order" and "Work Order". There is no
     document endpoint behind either — like View, both are built from the record
     in the browser. */
  const status = String(detail?.status ?? "").toUpperCase();
  const isFinal = ["APPROVED", "COMPLETED"].includes(status);
  /* Final Work Order: every approver has approved AND PR No., Commencement
     Date and Completion Date are filled in. A chain with no approver rows
     read falls back to the work order's own status. */
  const approverSteps = routing.filter((row: any) => String(row.role ?? "").toUpperCase().includes("APPROVER"));
  const allApproversApproved = approverSteps.length
    ? approverSteps.every((row: any) => hasApproved(row.status))
    : isFinal;
  const missingForFinal = [
    ["PR No.", detail?.prNo],
    ["Commencement Date", detail?.commencementDate],
    ["Completion Date", detail?.completionDate],
  ]
    .filter(([, value]) => !String(value ?? "").trim() || String(value).trim() === "-")
    .map(([label]) => label);
  const canOpenFinal = allApproversApproved && missingForFinal.length === 0;
  const finalHint = !allApproversApproved
    ? "Available once every approver has approved"
    : missingForFinal.length
    ? `Fill in ${missingForFinal.join(", ")} first`
    : undefined;
  /* The draft copy is available once someone in the chain has approved. */
  const anyoneApproved =
    isFinal || routing.some((row: any) => hasApproved(row.status));
  const openDocument = (draft: boolean) =>
    Promise.resolve(createDraftWorkOrderPdf(detail, draft ? "Draft Work Order" : "Work Order"))
      .then((doc) =>
        doc.getBlob((blob: Blob) =>
          setPreview({
            name: `${draft ? "Draft" : "Final"}-${fileName}`,
            url: URL.createObjectURL(blob),
          })
        )
      )
      .catch(documentFailed);

  /* v2.1 numbers read WO/PW/Central/01 — a "/" is not allowed in a file name. */
  const fileName = `WorkOrder-${String(detail?.workOrderNo ?? envelopeId ?? "document").replace(/[\\/:*?"<>|]+/g, "-")}.pdf`;
  const [sentOpen, setSentOpen] = useState(false);
  const { getPanelUser } = useHasPanelAccess();

  /* Actions -> Send: the work order PDF (as View / Download build it) mailed
     through POST /work-order/mail-attachment, in e-POD's request shape. */
  const sendDocument = async (mail: OutgoingMail) => {
    const doc = await workOrderDocument();
    const baseString = await new Promise<string>((resolve) => doc.getBase64(resolve));
    return workOrderApi.mailAttachment({
      mailSubject: mail.subject,
      mailMessage: mail.message,
      mails: mail.to,
      mailsCC: mail.cc,
      mailsBCC: mail.bcc,
      senderEmail: getPanelUser()?.email,
      baseString,
      pdfKey: String(detail?.projectId ?? detail?.workOrderNo ?? envelopeId ?? ""),
      pdfName: fileName.replace(/\.pdf$/i, ""),
    });
  };

  const dropdownItems: MenuProps["items"] = [
    { key: "download", label: "Download" },
    { key: "print", label: "Print" },
    { key: "view", label: "View" },
    { key: "send", label: "Send" },
  ];

  const onActionClick = ({ key }: { key: string }) => {
    setActionsOpen(false);
    if (key === "send") {
      setSendOpen(true);
      return;
    }
    if (key === "print") {
      /* Opened now, while the click still counts as the user's: a window
         opened after the comments/logs reads would be popup-blocked. */
      const printWindow = window.open("", "_blank");
      workOrderDocument()
        .then((doc) => doc.print({}, printWindow))
        .catch(() => {
          printWindow?.close();
          documentFailed();
        });
      return;
    }
    if (key === "view") {
      workOrderDocument()
        .then((doc) => doc.getBlob((blob: Blob) => setPreview({ name: fileName, url: URL.createObjectURL(blob) })))
        .catch(documentFailed);
      return;
    }
    if (key === "download") {
      workOrderDocument()
        .then((doc) =>
          doc.getBlob((blob: Blob) => {
            const url = URL.createObjectURL(blob);
            const anchor = window.document.createElement("a");
            anchor.href = url;
            anchor.download = fileName;
            window.document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            /* Released on the next tick — revoking immediately can cancel the
               save in some browsers. */
            setTimeout(() => URL.revokeObjectURL(url), 1000);
          })
        )
        .catch(documentFailed);
    }
  };

  const items = [
    {
      key: "projectInformation",
      label: "Project Information",
      children: (
        <ProjectInformation
          detail={detail}
          routing={routing}
          envelopeId={envelopeId}
          onActed={() => setRefreshKey((key) => key + 1)}
        />
      ),
    },
    {
      key: "comments",
      label: "Comments",
      children: (
        <Comments
          envelopeId={envelopeId}
          refreshKey={refreshKey}
        />
      ),
    },
    {
      key: "documentVersions",
      label: "Document Versions",
      children: (
        <DocumentVersions
          detail={detail}
          envelopeId={documentEnvelopeId}
          isDraft={
            documentsDraft ||
            String(detail?.status ?? "").toUpperCase() === "DRAFT" ||
            /-draft$/i.test(envelopeId ?? "")
          }
          workOrderPdf={(customDetail?: any) => workOrderDocument(customDetail)}
          fileName={fileName}
          refreshKey={refreshKey}
          onUploaded={() => setRefreshKey((key) => key + 1)}
        />
      ),
    },
    {
      key: "logs",
      label: "Logs",
      children: (
        <Logs
          envelopeId={envelopeId}
          refreshKey={refreshKey}
        />
      ),
    },
  ];

  /* The two work-order buttons only make sense on the information tab —
     the other tabs are history views — and not for a draft, which has no
     approvals to issue a letter from. Mirrors how SPADrilldown hides its
     action row for draft records. */
  const showWorkOrderActions = activeTab === "projectInformation" && status !== "DRAFT";

  return (
    <ContentWrapper
      /* Full-page loader on the first load only: a refresh after an action
         (resend, comment, save) would unmount the tab and lose its success popup. */
      loading={isLoading && refreshKey === 0}
      customTitle={
        <Space style={{ alignItems: "center", lineHeight: 1 }}>
          <Icon
            onClick={() => navigate(backTo)}
            icon="akar-icons:arrow-left"
            className={styles.backIcon}
          />
          <Typography.Title
            level={3}
            style={{ margin: 0 }}
          >
            Project Details
          </Typography.Title>
        </Space>
      }
      actionItems={
        <Space>
          {showWorkOrderActions && (
            <Button
              disabled={!anyoneApproved}
              title={anyoneApproved ? undefined : "Available once someone has approved the work order"}
              onClick={() => openDocument(true)}
            >
              Draft Work Order
            </Button>
          )}
          {showWorkOrderActions && (
            <Button
              disabled={!canOpenFinal}
              title={finalHint}
              onClick={() => openDocument(false)}
            >
              Final&nbsp; Work Order
            </Button>
          )}
          {/* A draft has nothing to download, print or send yet. */}
          {status !== "DRAFT" && (
          <Dropdown
            menu={{ items: dropdownItems, onClick: onActionClick }}
            placement="bottomRight"
            trigger={["click"]}
            open={actionsOpen}
            onOpenChange={setActionsOpen}
            overlayClassName={styles.actionsMenu}
          >
            <Button
              type="ghost"
              className={styles.actionsButton}
            >
              <Space>
                Actions
                <Icon
                  icon={actionsOpen ? "mdi:chevron-up" : "mdi:chevron-down"}
                  inline
                />
              </Space>
            </Button>
          </Dropdown>
          )}
        </Space>
      }
    >
      <div className={styles.pillTabs}>
        {/* Unmounted when left, so Comments / Document Versions / Logs reopen
            on their default list — search, sort and page used to be kept. */}
        <Tabs
          destroyInactiveTabPane
          items={items}
          activeKey={activeTab}
          onChange={(key) => {
            /* Switching tab keeps the rest of the query (`from=approved`). */
            const params = new URLSearchParams(window.location.search);
            params.set("activeTab", key);
            navigate(`${window.location.pathname}?${params}`);
          }}
        />
      </div>

      <SendDocumentModal
        open={sendOpen}
        onCancel={() => setSendOpen(false)}
        onSend={sendDocument}
        onSent={() => {
          setSendOpen(false);
          setSentOpen(true);
        }}
      />

      <SentSuccessModal
        open={sentOpen}
        onClose={() => setSentOpen(false)}
      />

      <PDFViewer
        show={!!preview}
        setShow={() => {
          if (preview?.url.startsWith("blob:")) URL.revokeObjectURL(preview.url);
          setPreview(null);
        }}
        title={preview?.name ?? ""}
        url={preview?.url ?? ""}
        downloadable={/^(Draft|Final)-/.test(preview?.name ?? "")}
      />
    </ContentWrapper>
  );
};
